import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { Tooltip } from "antd";
import { styled } from '@mui/system';
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import copy from "copy-to-clipboard";
import { StatusDot } from '../../../../../Components/StyledComponent';
import FieldStatusIndicator, { getIndicatorNames, useFieldIndicator } from '../../../../../Components/FieldStatusIndicator';
import CustomIcon from '../../../../../Components/CustomIcon';
import { useToast } from '../../../../../Hooks/useToast';



const DeviceIDLink = ({
    record,
    config,
    iconButtonStyle = {},
    iconStyle = {},
    textStyle = {},
    onTextClick,
    disableCopy = false,
    disableActiveStatus = false,
    // Field status indicators to hang off the text. Passed by the call site (see
    // Pages/Inventory), falling back to anything the column config names.
    indicators
}) => {

    const { openNotification } = useToast();
    const idText = record?.[config?.dataIndex];
    const defaultText = config?.defaultText || '-';
    const isActive = record?.pingStatus?.toLowerCase() === 'active';
    const { showRootAsLink = true, disableLink = false } = config
    // Field status markers are anchored here rather than around the whole cell so the
    // marker tracks the text: a wide column would otherwise park it far to the right.
    // Renders nothing unless the org has the feature on and a rule matches.
    const indicatorName = getIndicatorNames(indicators ?? config?.indicators)[0];
    // Only whether this cell carries a marker at all, not which one - it drives layout,
    // so it must be the same for every row or the copy icon would move about again.
    // Called before the early return below to keep hook order stable.
    const { active: hasIndicator } = useFieldIndicator({ name: indicatorName, record });

    if (!idText) return <Typography variant="fw400s14lh24c3D4B5A">{defaultText}</Typography>;

    return (
        // With a marker in play the id text is followed by a marker of varying width, so
        // a copy icon that simply trails the text lands in a different place on every
        // row. Filling the cell and letting the text block grow pins it to the right
        // edge instead. Gated on the marker so every other table keeps its current
        // shrink-to-fit layout.
        <Box display="flex" alignItems="center" sx={hasIndicator ? { width: '100%' } : undefined}>
            <Box
                display="flex"
                alignItems="center"
                minWidth={0}
                sx={hasIndicator ? { flexGrow: 1 } : undefined}
            >
                {!disableActiveStatus && (
                    <Box sx={{ flexShrink: 0 }}>
                        <StatusDot color={isActive ? '#09B820' : '#8F8F8F'} />
                    </Box>
                )}
                <FieldStatusIndicator name={indicatorName} record={record}>
                    {disableLink ? (
                        <Typography
                            variant="fw400s14lh24c3D4B5A"
                            sx={{ flex: 1, minWidth: 0 }}
                        >
                            {idText}
                        </Typography>
                    ) : (
                        <Typography
                            variant="fw400s14lh24c0061CA"
                            onClick={() => (onTextClick ? onTextClick() : {})}
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                color: showRootAsLink ? '' : '#3D4B5A !important',
                                ':hover': onTextClick
                                    ? {
                                        color: '#4D44D8 !important',
                                        cursor: 'pointer',
                                    }
                                    : {},
                            }}
                        >
                            {idText}
                        </Typography>
                    )}
                </FieldStatusIndicator>
            </Box>

            {!disableCopy && <Tooltip title={"Click to copy"} placement={'top'}>
                <IconButton
                    disableRipple
                    disableTouchRipple
                    disableFocusRipple
                    style={{
                        ...iconButtonStyle
                    }}
                    onClick={(event) => {
                        event.stopPropagation();
                        copy(idText);
                        openNotification({ text: 'Copied to Clipboard' })
                    }}
                >
                    <CustomIcon name="copy-icon" />
                </IconButton>
            </Tooltip>}
        </Box>
    );
};

export default DeviceIDLink;
